import { MutationController } from '@lit-labs/observers/mutation-controller.js';
import { LitElement, css, html } from 'lit'
import { customElement, property, queryAssignedElements } from 'lit/decorators.js'
import { styleMap } from 'lit/directives/style-map.js'

export type EPPaywallActivationState = 'on' | 'off';

@customElement('ep-paywall')
export class EPPaywall extends LitElement {
  private slotObserver = new MutationController(this, { 
    target: this,
    callback: () => this._handleSlotObservation(),
    config: { attributes: true, attributeFilter: ['style'] }
  })

  @property({ reflect: true })
  state: EPPaywallActivationState = 'on';

  @property({ type: Boolean, attribute: 'slot-controls-state', reflect: true })
  slotControlsState: boolean = false;

  @queryAssignedElements({ slot: 'paywall', flatten: true })
  slottedPaywall?: HTMLElement[];

  get active(): boolean {
    return this.state === 'on';
  }
  set active(value: boolean) {
    this.state = value ? 'on' : 'off';
  }

  get slottedPaywallElement(): HTMLElement | undefined {
    if (this.slottedPaywall && this.slottedPaywall.length >= 1) {
      return this.slottedPaywall[0];
    }
    return undefined;
  }

  render() {
    return html`
      <slot name="paywall" style=${styleMap({ display: this.active ? 'contents' : 'none' })} @slotchange=${this._handleSlotChange}></slot>
      <slot style=${styleMap({ pointerEvents: this.active ? 'none' : '' })}></slot>
    `
  }

  private _handleSlotChange() {
    if (this.slottedPaywallElement) {
      this.slotObserver.observe(this.slottedPaywallElement);
    }
  }

  private _handleSlotObservation() {
    if (this.slotControlsState && this.slottedPaywallElement) {
      const computedDisplay = window.getComputedStyle(this.slottedPaywallElement).display;
      this.active = computedDisplay !== 'none';
    }
  }

  static styles = css`
    :host {
      display: block;
      position: relative;
      --font-prose: ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
    }

    ::slotted([slot="paywall"]) {
      position: absolute;
      width: 100%;
      height: 100%;
      backdrop-filter: blur(5px);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 10px;
      box-sizing: border-box;
      z-index: 999;
    }

  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-paywall': EPPaywall
  }
}
